// IndexedDB layer. Two object stores:
//   articles: id PK, the full ManifestEntry payload
//   meta:     key/value (last_manifest_fetch_ms, schema_version, generated_at,
//             anonymous_id)
//
// `idb` keeps the API small and promise-shaped; no leaky IDBRequest plumbing
// up into the app layer.

import { type DBSchema, type IDBPDatabase, openDB } from "idb";
import type { ArticleDetail, ManifestEntry } from "../feed/types";

const DB_NAME = "agg";
const DB_VERSION = 1;

interface AggDB extends DBSchema {
  articles: {
    key: number;
    value: ManifestEntry & Partial<ArticleDetail>;
    indexes: { "by-score": number; "by-published": number };
  };
  meta: {
    key: string;
    value: string | number;
  };
}

let cached: Promise<IDBPDatabase<AggDB>> | null = null;

export function getDb(): Promise<IDBPDatabase<AggDB>> {
  if (!cached) {
    cached = openDB<AggDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("articles")) {
          const store = db.createObjectStore("articles", { keyPath: "id" });
          store.createIndex("by-score", "score");
          store.createIndex("by-published", "published_at");
        }
        if (!db.objectStoreNames.contains("meta")) {
          db.createObjectStore("meta");
        }
      },
    });
  }
  return cached;
}

export async function replaceArticles(entries: ManifestEntry[]): Promise<void> {
  const db = await getDb();
  const tx = db.transaction("articles", "readwrite");
  await tx.store.clear();
  for (const e of entries) {
    await tx.store.put(e);
  }
  await tx.done;
}

export async function listArticles(): Promise<ManifestEntry[]> {
  const db = await getDb();
  // Index 'by-score' returns ascending; we want highest first.
  const tx = db.transaction("articles", "readonly");
  const items = await tx.store.index("by-score").getAll();
  await tx.done;
  return items.sort((a, b) => b.score - a.score);
}

export async function upsertArticleDetail(detail: ArticleDetail): Promise<void> {
  const db = await getDb();
  const tx = db.transaction("articles", "readwrite");
  const existing = await tx.store.get(detail.id);
  await tx.store.put({ ...existing, ...detail });
  await tx.done;
}

export async function getArticle(
  id: number,
): Promise<(ManifestEntry & Partial<ArticleDetail>) | undefined> {
  const db = await getDb();
  return db.get("articles", id);
}

export async function getMeta(key: string): Promise<string | number | undefined> {
  const db = await getDb();
  return db.get("meta", key);
}

export async function setMeta(key: string, value: string | number): Promise<void> {
  const db = await getDb();
  await db.put("meta", value, key);
}
